import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ZoomIn, X, Sparkles, BookOpen } from "lucide-react";
import { SiteShell } from "./Landing";
import { usePricing, dkk } from "../lib/pricing";
import { useScrollLock } from "../lib/menus";
import IMG from "../data/funktioner-images.json";

const src = (name, w) => `/funktioner/${name}-${w}.webp`;

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Funktioner – JaTak Pro";
    const set = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const old = el?.getAttribute("content");
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
      return () => (old == null ? el.remove() : el.setAttribute("content", old));
    };
    const undo = [
      set("name", "description", "Se alt, JaTak Pro kan: automatisk ja tak fra Facebook-kommentarer, varianter med lager, QR-udlevering, medarbejder-login, statistik, SMS og flere butikker på én konto."),
      set("property", "og:title", "Funktioner – JaTak Pro"),
      set("property", "og:description", "Fra første opslag til udleveret vare – samlet i én app."),
      set("property", "og:image", "https://jatakpro.dk/funktioner/og-funktioner.png"),
      set("property", "og:url", "https://jatakpro.dk/funktioner"),
    ];
    return () => { document.title = prev; undo.forEach((u) => u()); };
  }, []);
}

function Shot({ name, alt, onZoom, big }) {
  const m = IMG[name];
  const [s, b] = m.sizes;
  const pic = (
    <picture>
      <source type="image/webp" srcSet={`${src(name, s)} ${s}w, ${src(name, b)} ${b}w`}
        sizes={m.kind === "m" ? "300px" : m.kind === "a" ? "(max-width: 900px) 100vw, 540px" : big ? "(max-width: 900px) 100vw, 1000px" : "(max-width: 900px) 100vw, 560px"} />
      <img src={`/funktioner/${name}-${b}.jpg`} width={m.w} height={m.h} alt={alt} loading={big ? "eager" : "lazy"} decoding="async" />
    </picture>
  );
  return (
    <figure className={`ft-shot ft-${m.kind}${big ? " ft-big" : ""}`} data-testid={`ft-shot-${name}`}>
      {m.kind === "m" ? <div className="ft-phone"><span className="ft-notch" aria-hidden="true" />{pic}</div>
        : m.kind === "a" ? <div className="ft-asset">{pic}</div> : <div className="ft-frame">{pic}</div>}
      <button type="button" className="ft-zoom" onClick={() => onZoom({ name, alt })} aria-label={`Forstør billede: ${alt}`} data-testid={`ft-zoom-${name}`}>
        <ZoomIn size={18} />
      </button>
    </figure>
  );
}

function Lightbox({ shot, onClose }) {
  useScrollLock(!!shot);
  useEffect(() => {
    if (!shot) return;
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [shot, onClose]);
  if (!shot) return null;
  const m = IMG[shot.name];
  return (
    <div className="ft-lightbox" role="dialog" aria-modal="true" aria-label={shot.alt} onClick={onClose} data-testid="ft-lightbox">
      <button type="button" className="ft-lb-close" onClick={onClose} aria-label="Luk" data-testid="ft-lightbox-close" autoFocus><X size={22} /></button>
      <img src={src(shot.name, m.sizes[1])} alt={shot.alt} className={m.kind === "m" ? "is-phone" : ""} />
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="ft-bullets">
      {items.map((t) => <li key={t}><span className="ft-check" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>{t}</li>)}
    </ul>
  );
}

const features = (p) => [
  { id: "ja-tak", title: "Automatisk ja tak fra Facebook-kommentarer", img: "ja-tak", alt: "Facebook-opslag med kommentarer, hvor JaTak Pro har svaret og registreret reservationer",
    text: "Når kunderne skriver ja tak under dit opslag, registrerer JaTak Pro reservationen, svarer og liker kommentaren og sender en besked på Messenger. Er varen udsolgt, kommer kunden på venteliste.",
    bullets: ["Forstår antal, farve og størrelse – fx »ja tak 2 sort str. M«", "Svar, like og Messenger-besked med det samme", "Automatisk venteliste, når varen er udsolgt"] },
  { id: "varianter", title: "Varianter med eget lager", img: "varianter", alt: "Oversigt over varianter i farver og størrelser med antal solgt og på lager",
    text: "Sælg den samme vare i flere farver og størrelser i ét opslag. Hver variant har sit eget lager, som tæller ned, når kunderne reserverer.",
    bullets: ["Farver og størrelser i ét opslag", "Lageret tæller ned automatisk", "Udsolgt pr. variant – resten kan stadig købes"] },
  { id: "kundens-side", title: "Kundens side med QR-kode", img: "kundens-side", alt: "Kundens ordreside på mobilen med QR-kode, afhentningstid og knap til afbud",
    text: "Kunden får et link til sin egen ordreside med QR-kode og afhentningstid. Her kan kunden også selv melde afbud, så varen går videre til den næste.",
    bullets: ["QR-kode til hurtig udlevering", "Ordre og afhentningstid samlet ét sted", "Kunden kan selv melde afbud"] },
  { id: "udlevering", title: "Udlevering med QR-scanner", img: "udlevering", alt: "Udlevering på mobilen med butiksnavn, kodefelt, scanner og liste over kunder",
    text: "Scan kundens QR-kode med telefonen, eller skriv koden. Ordren markeres som hentet med ét tryk – og kan fortrydes, hvis I kom til at trykke forkert.",
    bullets: ["Scanner, kodefelt og søgning på navn", "Fortryd og »ikke hentet« med ét tryk", "Butiksnavnet står tydeligt, så I ikke blander butikker sammen"] },
  { id: "personale", title: "Medarbejder-login med PIN", img: "personale", alt: "Login for medarbejdere med butikskode, brugernavn og PIN-kode",
    text: "Giv personalet eget login med butikskode, brugernavn og PIN. Medarbejdere har kun adgang til udlevering – ikke til tilbud, kunder eller indstillinger.",
    bullets: ["Ubegrænset antal medarbejdere", "Kun adgang til udlevering", "Loggen viser, hvem der har udleveret"] },
  { id: "kunder", title: "Kunder og noter", img: "kunder", alt: "Kundeliste med ordrer, afhentningsprocent og udeblivelser",
    text: "Se hver kundes ordrer, afhentningsprocent og udeblivelser. Skriv noter om kunden, og bloker automatisk kunder, der gentagne gange ikke henter.",
    bullets: ["Afhentningsprocent og udeblivelser", "Noter vises ved udlevering", "Automatisk blokering efter et antal udeblivelser"] },
  { id: "statistik", title: "Statistik", img: "statistik", alt: "Statistik med omsætning over tid, sammenligning med forrige periode og bedste tilbud",
    text: "Følg omsætning og ordrer pr. dag, uge og måned – sammenlignet med forrige periode. Se dine bedste tilbud, og hvornår kunderne siger ja tak.",
    bullets: ["Vælg periode og sammenlign", "Bedste tilbud og varianter", "Heatmap over hvornår kunderne siger ja tak"] },
  { id: "push", title: "Push-beskeder og app på telefonen", img: "push", alt: "Indstillinger for push-beskeder med valg af beskeder og stille timer",
    text: "Installer JaTak Pro som app på telefonen direkte fra browseren. Få en push-besked, når der kommer nye ja tak – og vælg selv, hvilke beskeder du vil have.",
    bullets: ["App på hjemmeskærmen – uden App Store", "Push ved nye ja tak", "Stille timer mellem 22 og 07"] },
  { id: "sms", title: "SMS-påmindelser", img: "sms", alt: "SMS-indstillinger med forbrug af inkluderede SMS i måneden",
    text: `Kunder, der selv skriver deres mobilnummer, kan få en SMS, når varen er klar, og en påmindelse inden afhentningen lukker. ${p.sms_included} SMS pr. butik pr. måned er inkluderet.`,
    bullets: ["»Klar til afhentning« og »husk at hente«", `${p.sms_included} SMS pr. butik pr. måned inkluderet`, "Slået fra som standard – du bestemmer"] },
  { id: "arkiv", title: "Genbrug tilbud og arkiv", img: "arkiv", alt: "Arkiv over tidligere tilbud med ordrer og omsætning",
    text: "Sælger du den samme vare igen, genbruger du det gamle tilbud med ét klik. I arkivet ser du alle tidligere tilbud med ordrer og omsætning.",
    bullets: ["Genbrug et tilbud med ét klik", "Omsætning og ordrer pr. tilbud", "Sortér og find gamle tilbud hurtigt"] },
  { id: "gdpr", title: "Sikkerhed og GDPR", img: "gdpr", alt: "Aftaler og data med vilkår, privatlivspolitik og databehandleraftale",
    text: "JaTak Pro er bygget til GDPR. Databehandleraftalen indgår i vilkårene, og kundedata slettes automatisk efter 6 måneders inaktivitet.",
    bullets: ["Databehandleraftale indgår automatisk", "Automatisk sletning efter 6 måneders inaktivitet", "Ingen binding – opsig når som helst"] },
];

export default function FeaturesPage() {
  useSeo();
  const p = usePricing();
  const [zoom, setZoom] = useState(null);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" }));
  }, []);
  return (
    <SiteShell>
      <section className="ft-hero" data-testid="features-hero">
        <span className="eyebrow">Funktioner</span>
        <h1>Alt, hvad dit ja tak-salg skal kunne</h1>
        <p className="ft-sub">Fra første opslag til udleveret vare – samlet i én app.</p>
      </section>

      <section className="ft-highlight" data-testid="ft-multi">
        <div className="ft-hl-text">
          <span className="ft-new"><Sparkles size={13} /> Nyhed</span>
          <h2>Flere butikker på én konto</h2>
          <p>
            Skift mellem butikkerne uden at logge ud. Hver butik har sin egen Facebook-side, egne medarbejdere og egen statistik – og I får én samlet
            faktura med {dkk(p.price_monthly_dkk)} kr./md. pr. butik.
          </p>
          <Bullets items={["Skift butik med ét klik", "Én samlet faktura", "Hver butik har egen Facebook-side og personale"]} />
        </div>
        <Shot name="flere-butikker" big alt="Butiksskifteren åben med to butikker over siden Abonnement med en samlet faktura" onZoom={setZoom} />
      </section>

      <section className="ft-highlight ft-pm" id="paamindelse" data-testid="ft-paamindelse">
        <div className="ft-hl-text">
          <span className="ft-new"><Sparkles size={13} /> Nyhed · Påmindelse i kommentaren</span>
          <div className="ft-stat" data-testid="ft-paamindelse-stat">
            <b>Få op til 30 %</b>
            <span>af den uafhentede ja tak-omsætning hjem</span>
          </div>
          <small className="ft-foot" data-testid="ft-paamindelse-footnote">Baseret på tal fra JaTak Pro-butikker, okt.–nov. 2026. Resultater varierer.</small>
          <h2>Glemte de at hente? Botten minder dem om det – helt automatisk</h2>
          <p>
            På sidste afhentningsdag svarer botten på kundens egen ja tak-kommentar – 1 time før afhentningen åbner. Kun kunder, der ikke har hentet
            endnu, får en påmindelse, og teksten varierer, så det ikke ligner spam. Ingen SMS-udgift.
          </p>
          <Bullets items={["Kun dem, der ikke har hentet", "Sendes 1 time før afhentning – aldrig før kl. 9",
            "Se præcis hvor mange kroner påmindelserne har hentet hjem", "Slå til og fra pr. butik og pr. tilbud"]} />
          <Link className="btn jatak big ft-pm-cta" to="/signup" data-testid="ft-paamindelse-signup">Prøv gratis i {p.trial_days} dage</Link>
        </div>
        <Shot name="paamind-kommentar" alt="Facebook-kommentartråd, hvor botten først bekræfter reservationen og på sidste afhentningsdag minder kunden om at hente" onZoom={setZoom} />
        <div className="ft-pm-more">
          <Shot name="paamind-3-trin" alt="Tre trin: kunden skriver ja tak, reservationen bekræftes, påmindelse på sidste afhentningsdag" onZoom={setZoom} />
          <Shot name="paamind-indstilling" alt="Indstillingen Påmind i kommentaren på sidste afhentningsdag slået til i JaTak Pro" onZoom={setZoom} />
        </div>
      </section>

      <div className="ft-rows">
        {features(p).map((f, i) => (
          <section key={f.id} className={"ft-row" + (i % 2 ? " flip" : "")} data-testid={`ft-row-${f.id}`}>
            <div className="ft-text">
              <h2>{f.title}</h2>
              <p>{f.text}</p>
              <Bullets items={f.bullets} />
            </div>
            <Shot name={f.img} alt={f.alt} onZoom={setZoom} />
          </section>
        ))}
      </div>

      <section className="cta-band ft-cta" data-testid="features-cta">
        <h2>Prøv JaTak Pro gratis i {p.trial_days} dage</h2>
        <p>Uden kort og uden binding.</p>
        <div className="pr-cta">
          <Link className="btn jatak big" to="/signup" data-testid="features-signup">Opret gratis konto</Link>
          <Link className="btn big" to="/priser" data-testid="features-pricing">Se priser</Link>
        </div>
        <a className="ft-guide" href="/guide/" data-testid="features-guide"><BookOpen size={16} /> Læs guiden</a>
      </section>
      <Lightbox shot={zoom} onClose={() => setZoom(null)} />
    </SiteShell>
  );
}
